import React, { useEffect, useState } from "react";
import { LassoSimpleButtonFull, LassoToggle } from "../../components/ui/utils/LassoButtons";
import TopMostPortal from "../../components/ui/utils/Portals";
import RestAPI from "../../utils/restAPI";
import { logError } from "../../shared/common/logger";

// Feature flags configuration
export const FEATURE_FLAGS = [
	{ key: "SHOW_DOCUSKETCH_UPLOAD", label: "DocuSketch Upload" },
	{ key: "SCOPE_NOTE_UPLOAD_CONFIG", label: "Scope Note Upload Config" },
	{ key: "RESPONSIVE_NOTE_ENABLED", label: "Responsive Note Enabled" },
	{ key: "RESPONSIVE_NOTES_BLANK_NOTE_ENABLED", label: "Responsive Notes Blank Note Enabled" },
	{ key: "RESPONSIVE_NOTE_CAMERA_CAPTURE", label: "Responsive Note Camera Capture" },
	{ key: "REESE_RESPONSIVE_NOTE_ENABLED", label: "Reese Enabled in Responsive Note (blank + templated)" },
	{ key: "HIDE_LEGACY_NOTE_BUTTON", label: "Hide Beta badge + old note button" },
	{ key: "MITIGATION_ENABLED", label: "Mitigation Enabled" },
	{ key: "FIRE_MITIGATION_ENABLED", label: "Fire Mitigation Enabled" },
	{ key: "MOLD_ENABLED", label: "Mold Enabled" },
	{ key: "CONTENTS_ENABLED", label: "Contents Enabled" },
	{ key: "BIOHAZARD_ENABLED", label: "Biohazard Enabled" },
	{ key: "TIME_AND_MATERIAL_ENABLED", label: "Time and Material Enabled" },
	{ key: "XCELERATE_ID_ENABLED", label: "Xcelerate ID Enabled" },
	{ key: "MIT_TO_RECON", label: "Mit-to-Recon Conversion" },
	{ key: "MOLD_TO_RECON", label: "Mold-to-Recon Conversion" },
	{ key: "TIC_SHEETS", label: "Tic Sheets" },
	{ key: "MOBILE_EXCEL_UPLOAD", label: "Mobile Excel Upload" },
	{ key: "MOBILE_ADDRESS_UPLOAD", label: "Mobile Address Upload" },
	{ key: "RECON_VOICE_AGENT_ENABLED", label: "Recon Voice Agent Enabled" },
	{ key: "PDF_EXPORT_ENABLED", label: "PDF Export Enabled" },
	{ key: "MOBILE_PDF_EXPORT_ENABLED", label: "Mobile PDF Export Enabled" },
	{ key: "PDF_PREVIEW_MOBILE_ENABLED", label: "PDF Preview Mobile Enabled" },
	{ key: "PDF_EXPORT_VERBOSE", label: "PDF Export Verbose Mode" },
	{ key: "PDF_EXPORT_PHOTO_REPORT", label: "PDF Export Photo Report" },
	{ key: "V2_UI", label: "V2 UI enable for mobile app" },
	{ key: "V2_MOBILE_APP_ESTIMATE_DETAILS_VIEW", label: "V2 Mobile App Estimate Details View" },
	{ key: "V2_MOBILE_APP_ON_DEVICE_TRANSCRIPTION", label: "V2 Mobile App On Device Transcription" },
	{ key: "OFFLINE_MODE_ENABLED", label: "V2 Enable Offline mode support for mobile app" },
	{ key: "V2_MOBILE_FORCE_MIT_MODULES", label: "V2 Mobile App Force Mit Modules (non-Reese)" },
	{ key: "LINE_ITEM_FEEDBACK", label: "Line Item Feedback" },
	{ key: "V2_ARCHIVE_PROJECT_MOBILE", label: "V2 Archive Project Mobile" },
	{ key: "ALLOW_USER_TO_SAVE_INVALID_FORMULAS", label: "Allow User To Save Invalid Formulas" },
	{ key: "SELF_SERVE_ADMIN_ENABLED", label: "Self-Serve Admin (Manage Users)" },
	{ key: "MANAGE_OFFICES_ENABLED", label: "Manage Offices (requires Self-Serve Admin)" },
	{ key: "USE_Q_A_REESE_SUMMARY", label: "Use Q&A Reese Summary" },
	{ key: "WEBAPP_ROOM_DIMENSIONS_ENABLED", label: "Webapp Room Dimensions Enabled" },
	{ key: "WEBAPP_TPA_AND_CARRIER_UPLOAD_ENABLED", label: "TPA and carrier selections activated in upload page for webapp" },
	{ key: "SPACES_ENABLED", label: "Spaces Enabled" },
	{ key: "PHOTO_LABELS_ENABLED", label: "Photo Labels Enabled" },
	{ key: "KEEP_FORMULAS_EXPANDED", label: "Keep Line Item Formulas Expanded (i.e. show the math), instead of combining the math/terms" },
	{
		key: "ENABLE_FLR_RPL_FOR_FORMULA_GENERATION",
		label: "The formula generation prompt can generate formulas with FLR_RPL as a valid shorthand.",
	},
	{ key: "FLOOR_PLAN_SCAN_ENABLED", label: "Floor Plan Scan Enabled" },
	{ key: "VIBE_ESTIMATING_ENABLED", label: "Vibe Estimating Agent (webapp)" },
	{ key: "DASHBOARD_DEFAULT_ASSIGNEE_TO_SELF", label: "Dashboard Default Assignee Filter to Self" },
];

interface FeatureFlagsModalProps {
	organizationId: string;
	onClose: () => void;
	onSave: (flags: { [key: string]: boolean }) => Promise<void>;
}

const FeatureFlagsModal: React.FC<FeatureFlagsModalProps> = ({ organizationId, onClose, onSave }) => {
	// Initialize with all flags from FEATURE_FLAGS, defaulting to false if not present
	const initializeFlags = (flags: { [key: string]: boolean }) => {
		const allFlags: { [key: string]: boolean } = {};
		FEATURE_FLAGS.forEach((flag) => {
			allFlags[flag.key] = flags[flag.key] || false;
		});
		return allFlags;
	};

	const [localFlags, setLocalFlags] = useState<{ [key: string]: boolean }>(() => {
		const initialFlags: { [key: string]: boolean } = {};
		FEATURE_FLAGS.forEach((flag) => {
			initialFlags[flag.key] = false;
		});
		return initialFlags;
	});
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);

	// Fetch feature flags when modal opens
	useEffect(() => {
		const fetchFeatureFlags = async () => {
			setLoading(true);
			try {
				const response = await RestAPI.fetchAPI(`/rest/access/organization/${organizationId}/features`, "GET");
				console.log("response", response);
				// Clear and rebuild with only FEATURE_FLAGS values
				const allFlags: { [key: string]: boolean } = {};
				FEATURE_FLAGS.forEach((flag) => {
					// Only include flags that exist in FEATURE_FLAGS
					// Use the value from response if it exists and is true, otherwise false
					if (response && response.featureFlags) {
						allFlags[flag.key] = response.featureFlags[flag.key] === true;
					} else {
						allFlags[flag.key] = false;
					}
				});
				setLocalFlags(allFlags);
			} catch (err) {
				logError("Error fetching feature flags", err);
				// Initialize with all flags disabled on error
				const initialFlags: { [key: string]: boolean } = {};
				FEATURE_FLAGS.forEach((flag) => {
					initialFlags[flag.key] = false;
				});
				setLocalFlags(initialFlags);
			} finally {
				setLoading(false);
			}
		};

		fetchFeatureFlags();
	}, [organizationId]);

	const toggleFlag = (key: string) => {
		setLocalFlags((prev) => ({
			...prev,
			[key]: !prev[key],
		}));
	};

	const handleSave = async () => {
		setSaving(true);
		try {
			// Clear and rebuild the object with only FEATURE_FLAGS values
			// Set all flags to false first, then apply the values from localFlags
			const allFlagsToSave: { [key: string]: boolean } = {};
			FEATURE_FLAGS.forEach((flag) => {
				// Only include flags that exist in FEATURE_FLAGS
				// Use the value from localFlags if it exists, otherwise false
				allFlagsToSave[flag.key] = localFlags[flag.key] === true;
			});
			console.log("allFlagsToSave", allFlagsToSave);
			await onSave(allFlagsToSave);
		} finally {
			setSaving(false);
		}
	};

	return (
		<TopMostPortal>
			<div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
				<div className="bg-white w-[840px] max-h-[600px] rounded-xl shadow-lg flex flex-col">
					<div className="flex w-full h-12 bg-gray-100 rounded-t-xl justify-between items-center px-4 border-b border-gray-200">
						<div className="text-gray-900 text-lg font-semibold font-['Inter'] leading-7">Feature Flags</div>
						<button onClick={onClose} className="cursor-pointer p-1 hover:bg-gray-200 rounded">
							<img className="w-5 h-5" src="/assets/ui/x.svg" alt="Close" />
						</button>
					</div>
					<div className="flex-1 overflow-y-auto p-6 bg-gray-50">
						<div className="mb-4 text-sm text-gray-600">Organization ID: {organizationId}</div>
						{loading ? (
							<div className="flex items-center justify-center h-64">
								<div className="text-gray-600">Loading feature flags...</div>
							</div>
						) : saving ? (
							<div className="flex items-center justify-center h-64">
								<div className="text-gray-600">Saving feature flags...</div>
							</div>
						) : (
							<div className="space-y-4">
								{FEATURE_FLAGS.map((flag) => (
									<div
										key={flag.key}
										className="flex items-center justify-between p-4 bg-white rounded-lg border border-gray-200"
									>
										<div className="flex flex-col">
											<div className="text-gray-900 text-base font-medium font-['Hanken Grotesk']">{flag.label}</div>
											<div className="text-gray-500 text-sm font-['Hanken Grotesk'] mt-1">{flag.key}</div>
										</div>
										<LassoToggle
											checked={localFlags[flag.key] || false}
											onChange={() => toggleFlag(flag.key)}
											disabled={loading || saving}
										/>
									</div>
								))}
							</div>
						)}
					</div>
					<div className="w-full h-20 bg-white border-t border-gray-200 flex items-center justify-end gap-4 px-6 rounded-b-xl">
						<LassoSimpleButtonFull
							caption="Cancel"
							onClick={onClose}
							customButtonStyle={{ backgroundColor: "white", border: "1px solid #EAECF0" }}
							customTextStyle={{ color: "#344054" }}
							disabled={loading || saving}
						/>
						<LassoSimpleButtonFull caption={saving ? "Saving..." : "Save"} onClick={handleSave} disabled={loading || saving} />
					</div>
				</div>
			</div>
		</TopMostPortal>
	);
};

export default FeatureFlagsModal;
